// @file    ih-tabbar.component.ts
// @created 2026-06-22 by Claude
// @desc    IHUB redesign bottom tab bar (5 items: Home · Policies · Pay (FAB) ·
//          Bonus · Profile). Lives in the shell so it persists across the main
//          tab screens; hidden on sub-pages via the route `hideTabBar` flag.
//          Active state is derived from the current URL (detail/menu screens map
//          back to their owning tab, mirroring the prototype's TAB_OF).
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LucideAngularModule } from 'lucide-angular';
import { FaToLucidePipe } from '../../core/icons/fa-to-lucide.pipe';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

interface Tab {
  id: string;
  label: string;      // i18n key
  icon: string;       // font-awesome class
  route: string;
  center?: boolean;   // raised FAB (Pay)
  matches: string[];  // url prefixes that highlight this tab
}

@Component({
  selector: 'app-ih-tabbar',
  standalone: true,
  imports: [CommonModule, TranslateModule, LucideAngularModule, FaToLucidePipe],
  templateUrl: './ih-tabbar.component.html'
})
export class IhTabbarComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  activeId = 'home';

  // Short labels for the narrow tabs (nav.payments / nav.bonus are too long).
  tabs: Tab[] = [
    { id: 'home',     label: 'nav.home',     icon: 'fa-solid fa-house',       route: '/ih-home',          matches: ['/ih-home'] },
    { id: 'policies', label: 'nav.policies', icon: 'fa-solid fa-file-shield', route: '/ih-policies',      matches: ['/ih-policies', '/ih-policy'] },
    { id: 'pay',      label: 'ihx.tabPay',   icon: 'fa-solid fa-wallet',      route: '/ih-pay',           center: true, matches: ['/ih-pay'] },
    { id: 'bonus',    label: 'ihx.qaBonus',  icon: 'fa-solid fa-gift',        route: '/ih-bonus-history', matches: ['/ih-bonus'] },
    { id: 'profile',  label: 'nav.profile',  icon: 'fa-solid fa-user',        route: '/ih-profile',       matches: ['/ih-profile'] },
  ];

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.setActive(this.router.url);
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((e: any) => this.setActive(e.urlAfterRedirects || e.url));
    // Tell the rest of the app a tab bar is present so the global fixed
    // "My QR Code" button lifts above it instead of overlapping (see
    // _ih-redesign.scss `body.ihx-has-tabbar app-qr-button`).
    document.body.classList.add('ihx-has-tabbar');
  }

  ngOnDestroy(): void {
    document.body.classList.remove('ihx-has-tabbar');
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setActive(url: string): void {
    const hit = this.tabs.find(t => t.matches.some(m => url.startsWith(m)));
    this.activeId = hit ? hit.id : '';
  }

  go(tab: Tab): void {
    this.router.navigate([tab.route]);
  }
}
